import React, { useEffect, useState, useCallback } from 'react';
import AdminLayout from '../components/AdminLayout';
import api from '../lib/api';
import { RefreshCw } from 'lucide-react';

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

const STATUS_COLOR = {
  filled: 'text-emerald-400 border-emerald-500/40 bg-emerald-500/5',
  partially_filled: 'text-emerald-300 border-emerald-500/30',
  submitted: 'text-amber-400 border-amber-500/40 bg-amber-500/5',
  skipped: 'text-zinc-400 border-white/10',
  failed: 'text-red-400 border-red-500/40 bg-red-500/5',
  rejected: 'text-red-400 border-red-500/40 bg-red-500/5',
  expired: 'text-amber-300 border-amber-500/30',
  canceled: 'text-zinc-400 border-white/10',
};

const FILTERS = ['all', 'filled', 'submitted', 'skipped', 'failed', 'rejected', 'expired'];

export default function AdminExecutions() {
  const [execs, setExecs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === 'all' ? {} : { status: filter };
      const { data } = await api.get('/admin/executions', { params });
      setExecs(data.items || []);
    } catch (e) {
      console.error('[admin/executions] load failed', e);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  return (
    <AdminLayout title="Executions" description="All per-user execution attempts across signals.">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex flex-wrap items-center gap-1.5" data-testid="admin-execs-filters">
          {FILTERS.map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`admin-execs-filter-${f}`}
              className={`text-xs px-3 py-1.5 border rounded-sm transition-all font-mono ${
                filter === f ? 'border-[#d8b34a]/50 text-white bg-[#d8b34a]/[0.06]' : 'border-white/10 text-zinc-400 hover:text-white'
              }`}
            >
              {f.toUpperCase()}
            </button>
          ))}
        </div>
        <button onClick={load} data-testid="admin-execs-refresh" className="inline-flex items-center gap-2 text-sm px-4 py-2 border border-white/10 text-zinc-300 hover:border-[#d8b34a]/50 hover:text-white rounded-sm transition-colors">
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="border border-white/10 rounded-md overflow-hidden bg-[#0c0c0e]" data-testid="admin-executions-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] border-b border-white/10">
              <tr className="text-left text-[10px] tracking-[0.3em] font-mono text-zinc-500">
                <th className="px-4 py-3">USER</th>
                <th className="px-4 py-3">SYMBOL</th>
                <th className="px-4 py-3">EVENT</th>
                <th className="px-4 py-3">SIDE</th>
                <th className="px-4 py-3">STATUS</th>
                <th className="px-4 py-3">REASON</th>
                <th className="px-4 py-3">QTY</th>
                <th className="px-4 py-3">LIMIT</th>
                <th className="px-4 py-3">ANCHOR · SRC</th>
                <th className="px-4 py-3">DRIFT</th>
                <th className="px-4 py-3">QUOTE SRC</th>
                <th className="px-4 py-3">NOTIONAL</th>
                <th className="px-4 py-3">ATTEMPTS</th>
                <th className="px-4 py-3">CREATED</th>
              </tr>
            </thead>
            <tbody>
              {execs.map(e => (
                <tr key={e.execution_id} className="border-t border-white/5 hover:bg-white/[0.02]">
                  <td className="px-4 py-3 text-zinc-100">{e.user_email}</td>
                  <td className="px-4 py-3 font-mono text-white">{e.symbol}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] font-mono px-2 py-0.5 border rounded-sm ${
                      e.event_type === 'exit' ? 'text-rose-300 border-rose-500/40 bg-rose-500/5' : 'text-sky-300 border-sky-500/40 bg-sky-500/5'
                    }`} title={e.event_type === 'exit' ? `Exit reason: ${e.exit_reason || 'unknown'}` : 'Entry'}>
                      {e.event_type === 'exit' ? `EXIT${e.close_pct ? ` ${Math.round(e.close_pct * 100)}%` : ''}` : 'ENTRY'}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono">
                    <span className={e.side === 'buy' ? 'text-emerald-400' : 'text-red-400'}>{e.side?.toUpperCase()}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] font-mono px-2 py-0.5 border rounded-sm ${STATUS_COLOR[e.status] || 'text-zinc-400 border-white/10'}`}>
                      {(e.status || '—').toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-400 max-w-[200px] truncate" title={e.skip_reason || e.error_message || ''}>{e.skip_reason || e.error_message || '—'}</td>
                  <td className="px-4 py-3 text-zinc-300">{e.qty ?? '—'}</td>
                  <td className="px-4 py-3 text-zinc-300">{e.limit_price ? `$${e.limit_price}` : '—'}</td>
                  <td className="px-4 py-3">
                    {e.anchor_price ? (
                      <div className="flex flex-col gap-0.5">
                        <span className="text-zinc-200 font-mono text-xs">${(+e.anchor_price).toFixed(2)}</span>
                        <span
                          className={`text-[9px] font-mono px-1.5 py-0.5 border rounded-sm w-fit ${
                            e.price_source === 'master_fill' ? 'text-emerald-300 border-emerald-500/40 bg-emerald-500/5'
                            : e.price_source === 'master_limit' ? 'text-sky-300 border-sky-500/40 bg-sky-500/5'
                            : e.price_source === 'quote' ? 'text-violet-300 border-violet-500/40 bg-violet-500/5'
                            : 'text-amber-300 border-amber-500/40 bg-amber-500/5'
                          }`}
                          title={
                            e.master_avg_fill_price ? `master_fill=$${e.master_avg_fill_price}`
                            : e.master_limit_price ? `master_limit=$${e.master_limit_price}`
                            : ''
                          }
                        >
                          {(e.price_source || 'unknown').toUpperCase().replace('_', ' ')}
                        </span>
                      </div>
                    ) : <span className="text-[10px] text-zinc-600">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    {e.difference_bps != null ? (
                      <span className={`text-[11px] font-mono ${
                        Math.abs(e.difference_bps) <= 20 ? 'text-emerald-400'
                        : Math.abs(e.difference_bps) <= 50 ? 'text-amber-400'
                        : 'text-red-400'
                      }`}>
                        {e.difference_bps > 0 ? '+' : ''}{e.difference_bps} bps
                      </span>
                    ) : <span className="text-[10px] text-zinc-600">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    {e.quote_source ? (
                      <span
                        title={e.last_quote_at ? `Last quote: ${e.last_quote_at}${e.quote_age_seconds != null ? ` (${Math.round(e.quote_age_seconds)}s old)` : ''}` : ''}
                        className={`text-[10px] font-mono px-2 py-0.5 border rounded-sm ${
                          e.quote_source === 'alpaca_quote' ? 'text-emerald-400 border-emerald-500/30 bg-emerald-500/5'
                          : e.quote_source === 'alpaca_trade' ? 'text-sky-300 border-sky-500/30 bg-sky-500/5'
                          : e.quote_source === 'reference_price_synthetic' ? 'text-amber-300 border-amber-500/30 bg-amber-500/5'
                          : 'text-zinc-500 border-white/10'
                        }`}
                      >
                        {e.quote_source === 'reference_price_synthetic' ? 'REF-PRICE' : e.quote_source.replace('alpaca_', '').toUpperCase()}
                      </span>
                    ) : <span className="text-[10px] text-zinc-600">—</span>}
                  </td>
                  <td className="px-4 py-3 text-zinc-300">{e.target_notional ? `$${(+e.target_notional).toFixed(2)}` : '—'}</td>
                  <td className="px-4 py-3 text-zinc-400">{e.attempts ?? '—'}</td>
                  <td className="px-4 py-3 text-xs text-zinc-500 font-mono whitespace-nowrap">{fmtDate(e.created_at)}</td>
                </tr>
              ))}
              {!loading && execs.length === 0 && (
                <tr><td colSpan={14} className="px-4 py-10 text-center text-sm text-zinc-500">No executions match this filter.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
